import React, { useCallback } from "react";
import { FXType } from "./FXDrawer";
import FXModule from "./FXModule";
import DropdownSelector from "./sequencer/components/DropdownSelector";

interface FilterControlProps {
  filterCutoff: number;
  onFilterCutoffChange: (value: number) => void;
  filterType: BiquadFilterType;
  onFilterTypeChange: (type: BiquadFilterType) => void;
  onFXSelect: (fx: FXType) => void;
  mappingMode?: string | null;
  fxModes?: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void;
  onFxModeChange?: (modes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onInitializeStepsWithGlobalValue?: (
    fxType: string,
    globalValue: number
  ) => void;
  hasMappingApplied?: boolean;
}

const FILTER_TYPE_OPTIONS = [
  { key: "lowpass", label: "Low Pass" },
  { key: "highpass", label: "High Pass" },
  { key: "bandpass", label: "Band Pass" },
  { key: "notch", label: "Notch" },
  { key: "allpass", label: "All Pass" },
];

const FILTER_MIN = 20;
const FILTER_MAX = 10000;

const FilterControl: React.FC<FilterControlProps> = ({
  filterCutoff,
  onFilterCutoffChange,
  filterType,
  onFilterTypeChange,
  onFXSelect,
  mappingMode,
  fxModes,
  onMappingModeChange,
  onFxModeChange,
  onInitializeStepsWithGlobalValue,
  hasMappingApplied = false,
}) => {
  // For filter, convert cutoff frequency (Hz) to UI control value (0-1) using logarithmic mapping
  const filterMix = useCallback(() => {
    // Reverse logarithmic mapping: Hz → 0-1
    if (filterCutoff <= FILTER_MIN) return 0;
    if (filterCutoff >= FILTER_MAX) return 1;
    return (
      Math.log(filterCutoff / FILTER_MIN) / Math.log(FILTER_MAX / FILTER_MIN)
    );
  }, [filterCutoff])();

  // Handle filter mix change
  const handleFilterMixChange = useCallback(
    (value: number) => {
      // Forward logarithmic mapping: 0-1 → Hz
      const newCutoff = FILTER_MIN * Math.pow(FILTER_MAX / FILTER_MIN, value);
      onFilterCutoffChange(newCutoff);
    },
    [onFilterCutoffChange]
  );

  // Format filter cutoff in Hz (receives Hz value directly)
  const formatFrequency = useCallback(
    (_value: number) => {
      // Value parameter is unused - we use filterCutoff prop directly
      const frequencyHz = filterCutoff;

      // Format based on value range
      if (frequencyHz >= 1000) {
        return `${(frequencyHz / 1000).toFixed(1)} kHz`;
      }
      return `${Math.round(frequencyHz)} Hz`;
    },
    [filterCutoff]
  );

  const handleGraphClick = useCallback(() => {
    if (onMappingModeChange) {
      // Toggle mapping mode: if already mapping filter, exit; otherwise enter filter mapping
      if (mappingMode === "filter") {
        onMappingModeChange(null);
      } else {
        onMappingModeChange("filter");
        // Switch to per-step mode if not already
        const wasGlobalMode = fxModes && !fxModes.filter;
        if (onFxModeChange && fxModes) {
          onFxModeChange({ ...fxModes, filter: true });
        }
        // Initialize steps with current global value when switching to per-step mode
        if (wasGlobalMode && onInitializeStepsWithGlobalValue) {
          onInitializeStepsWithGlobalValue("filter", filterCutoff);
        }
      }
    }
  }, [
    mappingMode,
    fxModes,
    onMappingModeChange,
    onFxModeChange,
    onInitializeStepsWithGlobalValue,
    filterCutoff,
  ]);

  const handleFilterTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterTypeChange(e.target.value as BiquadFilterType);
    },
    [onFilterTypeChange]
  );

  return (
    <FXModule
      name="Filter"
      mixValue={filterMix}
      onMixChange={handleFilterMixChange}
      color="#FF9800"
      onDrawerOpen={() => onFXSelect("filter")}
      label="Cutoff"
      valueFormatter={formatFrequency}
      onGraphClick={handleGraphClick}
      isPerStepMode={fxModes?.filter || false}
      isMappingActive={mappingMode === "filter"}
      hasMappingApplied={hasMappingApplied}
      hideValue
    >
      <DropdownSelector
        id="filter-type-selector"
        value={filterType}
        options={FILTER_TYPE_OPTIONS}
        onChange={handleFilterTypeChange}
        disabled={hasMappingApplied}
        minWidth="70px"
        variant="compactSmall"
      />
    </FXModule>
  );
};

export default FilterControl;
